import React from 'react';
import { CreateButton, Datagrid, FunctionField, List, NumberField, SelectInput, useCreatePath } from 'react-admin';
import { Link, useLocation } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckIcon from '@mui/icons-material/Check';
import CreateIcon from '@mui/icons-material/Create';
import DeleteNewsPostButton from '../DeleteNewsPostButton';
import { NEWS_POSTS_RESOURCE, STATUS_CHOICES, formatPeriod, htmlToText } from '../common';
import { StatusChip } from '../chips';
import { filledButtonSx } from '../buttons';

const EXCERPT_LENGTH = 90;

const excerpt = (html) => {
    const text = htmlToText(html).trim();
    return text.length > EXCERPT_LENGTH ? `${text.slice(0, EXCERPT_LENGTH).trimEnd()}…` : text;
};

const Thumbnail = ({ src }) => (
    <Box
        sx={{
            width: 56,
            height: 40,
            flexShrink: 0,
            borderRadius: '6px',
            overflow: 'hidden',
            bgcolor: 'background.default',
            color: 'text.disabled',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        {src ? (
            <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
            <CampaignIcon fontSize="small" />
        )}
    </Box>
);

const ellipsisSx = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// Celda «Publicación» del prototipo: título de 14 px y peso 500, extracto de 13 px en gris, ambos en una línea.
const PostCell = ({ record }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 260, maxWidth: 480 }}>
        <Thumbnail src={record.image_url} />
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...ellipsisSx, fontSize: 14, fontWeight: 500, color: '#111827' }}>{record.title?.es}</Typography>
            <Typography sx={{ ...ellipsisSx, fontSize: 13, color: '#6b7280' }}>{excerpt(record.body?.es)}</Typography>
        </Box>
    </Box>
);

/**
 * Columna «Acciones», solo con iconos: editar y eliminar. Ver el detalle es pulsar la fila
 * (`rowClick="show"`), así que no hay icono de ojo. Los dos cortan el clic para no abrir además la ficha.
 */
const RowActions = ({ record }) => {
    const createPath = useCreatePath();
    return (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }} onClick={(event) => event.stopPropagation()}>
            <Tooltip title="Editar">
                <IconButton
                    component={Link}
                    to={createPath({ resource: NEWS_POSTS_RESOURCE, type: 'edit', id: record.id })}
                    aria-label="Editar"
                    size="small"
                    color="primary"
                >
                    <CreateIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <DeleteNewsPostButton iconOnly />
        </Box>
    );
};

// Tabla como la del prototipo: tarjeta blanca con borde gris y radio 10, cabecera azul claro con texto
// de 12 px, peso 600 y el mismo gris en todas las columnas (también la ordenada), filas de 14 px.
const HEADER_TEXT = '#374151';
const datagridSx = {
    '& .RaDatagrid-headerCell': {
        bgcolor: '#E6F3FC',
        color: HEADER_TEXT,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        py: 1.5,
        borderBottom: 0,
    },
    '& .RaDatagrid-headerCell .MuiTableSortLabel-root, & .RaDatagrid-headerCell .MuiTableSortLabel-root.Mui-active, & .RaDatagrid-headerCell .MuiTableSortLabel-root:hover':
        { color: HEADER_TEXT, fontWeight: 600 },
    '& .RaDatagrid-headerCell .MuiTableSortLabel-icon': { color: `${HEADER_TEXT} !important`, fontSize: 16 },
    '& .RaDatagrid-rowCell': { fontSize: 14, color: HEADER_TEXT, py: '14px', borderBottom: '1px solid #e5e7eb' },
    '& .RaDatagrid-row:last-of-type .RaDatagrid-rowCell': { borderBottom: 0 },
    '& .RaDatagrid-headerCell:first-of-type, & .RaDatagrid-rowCell:first-of-type': { pl: '20px' },
    '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': { pr: '20px' },
};

const listSx = {
    '& .RaList-content': { border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: 'none', overflow: 'hidden' },
};

const newsFilters = [<SelectInput key="status" source="status" label="Estado" choices={STATUS_CHOICES} alwaysOn />];

const ListHeader = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="h1" sx={{ fontSize: 26, fontWeight: 600, m: 0 }}>
                Gestor de novedades
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14, maxWidth: 640 }}>
                Cada publicación se muestra como pop-up hasta 3 veces a cada usuario durante su vigencia y queda guardada en
                la sección Novedades.
            </Typography>
        </Box>
        <CreateButton label="Nueva publicación" variant="contained" icon={<AddIcon />} sx={filledButtonSx} />
    </Box>
);

/** Banner verde tras publicar; el texto llega en el `state` de la navegación desde el alta. */
const FlashBanner = () => {
    const flash = useLocation().state?.flash;
    if (!flash) return null;

    return (
        <Box
            role="status"
            sx={(theme) => ({
                display: 'flex',
                gap: 1.25,
                alignItems: 'flex-start',
                p: '12px 16px',
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.12),
                color: 'success.dark',
                fontSize: 14,
            })}
        >
            <CheckIcon fontSize="small" sx={{ flex: 'none', mt: '1px' }} />
            <span>{flash}</span>
        </Box>
    );
};

const Empty = () => (
    <Box sx={{ textAlign: 'center', p: 5, width: '100%', bgcolor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
            Todavía no hay novedades publicadas.
        </Typography>
    </Box>
);

const NewsPostList = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
        <ListHeader />
        <FlashBanner />
        <List
            title="Gestor de novedades"
            actions={false}
            filters={newsFilters}
            sort={{ field: 'starts_at', order: 'DESC' }}
            exporter={false}
            empty={<Empty />}
            sx={listSx}
        >
            <Datagrid rowClick="show" bulkActionButtons={false} sx={datagridSx}>
                <FunctionField label="Publicación" sortable={false} render={(record) => <PostCell record={record} />} />
                <FunctionField
                    label="Vigencia"
                    sortBy="starts_at"
                    render={(record) => <Box sx={{ whiteSpace: 'nowrap' }}>{formatPeriod(record.starts_at, record.ends_at)}</Box>}
                />
                <FunctionField label="Estado" sortable={false} render={(record) => <StatusChip status={record.status} />} />
                <NumberField source="metrics.users_reached" label="Usuarios alcanzados" sortable={false} />
                <NumberField source="metrics.impressions" label="Impresiones del pop-up" sortable={false} />
                <NumberField source="metrics.clicks" label="Abrieron Novedades" sortable={false} />
                <FunctionField
                    label="Acciones"
                    sortable={false}
                    textAlign="right"
                    render={(record) => <RowActions record={record} />}
                />
            </Datagrid>
        </List>
    </Box>
);

export default NewsPostList;
